import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Banner, Button, Card, Field, Input } from "@magarac/ui";
import { settingsBusinessSchema, type SettingsBusiness } from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/")({
  component: SettingsBusinessPage,
});

const empty: SettingsBusiness = { name: "", phone: "", email: "", website: "" };

/** Business details (settings.business). Screen key: settings.business */
function SettingsBusinessPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("business");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Business details</h1>
        <HelpIcon screen="settings.business" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <BusinessForm
          key={active?.id ?? "none"}
          initial={{ ...empty, ...(value ?? {}) }}
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function BusinessForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsBusiness;
  onSave: (v: SettingsBusiness) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [form, setForm] = useState<SettingsBusiness>(initial);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cleaned: SettingsBusiness = {
      name: form.name.trim(),
      ...(form.phone?.trim() ? { phone: form.phone.trim() } : {}),
      ...(form.email?.trim() ? { email: form.email.trim() } : {}),
      ...(form.website?.trim() ? { website: form.website.trim() } : {}),
    };
    const parsed = settingsBusinessSchema.safeParse(cleaned);
    if (!parsed.success) {
      setLocalError(
        "Please check the fields. The business name is required, and the website needs https://",
      );
      return;
    }
    setLocalError(null);
    void onSave(parsed.data);
  };

  return (
    <>
      {localError || error ? <Banner tone="danger">{localError ?? error}</Banner> : null}
      {saved && !localError ? <Banner tone="ok">Saved.</Banner> : null}
      <Card>
        <form onSubmit={submit} noValidate>
          <Field id="name" label="Business name">
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field id="phone" label="Phone" hint="The number your customers call today.">
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              value={form.phone ?? ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <Field id="email" label="Email">
            <Input
              id="email"
              type="email"
              inputMode="email"
              value={form.email ?? ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field id="website" label="Website" hint="Optional. Include https://">
            <Input
              id="website"
              type="url"
              inputMode="url"
              value={form.website ?? ""}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </Field>
          <Button type="submit" busy={busy}>
            Save
          </Button>
        </form>
      </Card>
    </>
  );
}
